import type {
  BuildWhiteBoardTokenRequest,
  CancelLiveStreamRequest,
  Category,
  ChannelDictionary,
  ChannelInfo,
  GenerateChannelRequest,
  GetChannelListRequest,
  GetChannelListResponse,
  KickOutResponse,
  Speaker,
  Topic
} from '@/models/interface/streaming';
import { ApiFactory, type ApiDataResponse } from '@/utils/api';
import Http from '@/utils/http';

const http = new Http();

export class StreamingService extends ApiFactory {
  constructor() {
    super('Streaming');
  }

  /**
   * 建立直播頻道
   * @param data 建立直播頻道所需的資料
   * @returns 直播頻道資訊
   */
  generateChannel(data: GenerateChannelRequest): Promise<ApiDataResponse<string>> {
    return super.post('GenerateChannel', data);
  }

  /**
   * 取得直播 Token
   * @param channelName 頻道名稱
   * @returns 直播 Token 資訊
   */
  buildTokenWithAccount(channelName: string): Promise<ApiDataResponse<string[]>> {
    return super.get('BuildTokenWithAccount', { channelName });
  }

  /**
   * 取得直播 RTM Token
   * @param channelName 頻道名稱
   * @returns 直播 RTM Token 資訊
   */
  buildTokenWithRtm(channelName: string): Promise<ApiDataResponse<string>> {
    return super.get('BuildTokenWithRtm', { channelName });
  }

  /**
   * 取得單一直播頻道資訊
   * @param channelName 頻道名稱
   * @returns 直播頻道詳細資訊
   */
  getChannel(channelName: string): Promise<ApiDataResponse<ChannelInfo>> {
    return super.get(`GetChannel/${channelName}`);
  }

  /**
   * 取得立即直播頻道對照人數表
   * @returns 頻道名稱對應觀看人數的字典
   */
  getChannelDictionary(): Promise<ApiDataResponse<ChannelDictionary>> {
    return super.get('GetChannelDictionary');
  }

  /**
   * 取得立即直播頻道清單
   * @param params 查詢參數
   * @returns 立即直播頻道清單
   */
  getImmediatelyChannelList(
    params?: GetChannelListRequest
  ): Promise<ApiDataResponse<GetChannelListResponse>> {
    return super.get('GetImmediatelyChannelList', params, {
      headers: {
        disableGlobalLoading: true
      }
    });
  }

  /**
   * 取得預約直播頻道清單
   * @param params 查詢參數
   * @returns 預約直播頻道清單
   */
  getAppointmentChannelList(
    params?: GetChannelListRequest
  ): Promise<ApiDataResponse<GetChannelListResponse>> {
    return super.get('GetAppointmentChannelList', params);
  }

  /**
   * 取得分類
   * @returns 分類清單
   */
  getCategories(): Promise<ApiDataResponse<Category[]>> {
    return super.get('GetCategories');
  }

  /**
   * 取得主題
   * @returns 主題清單
   */
  getTopics(): Promise<ApiDataResponse<Topic[]>> {
    return super.get('GetTopics');
  }

  /**
   * 取消直播
   * @param data 取消直播所需的資料
   * @returns 是否取消成功
   */
  cancelLiveStream(data: CancelLiveStreamRequest): Promise<ApiDataResponse<boolean>> {
    return super.put('CancelLiveStream', data);
  }

  /**
   * 取得直播Token
   * @param channelName 頻道名稱
   * @returns 直播Token資訊
   */
  buildTokenWithUID(channelName: string): Promise<ApiDataResponse<string[]>> {
    return super.get('BuildTokenWithUID', { channelName }, { params: { channelName } });
  }

  /**
   * 取得白板Token
   * @param params 取得白板Token所需的參數
   * @returns 白板Token
   */
  buildWhiteBoardToken(params: BuildWhiteBoardTokenRequest): Promise<ApiDataResponse<string>> {
    return super.get('BuildWhiteBoardToken', params);
  }

  getSpeakers(): Promise<ApiDataResponse<Speaker[]>> {
    return super.get('GetSpeakers');
  }

  /**
   * 將使用者踢出直播
   * @param channelName 頻道名稱
   * @param uid 使用者ID
   * @returns 踢出結果
   */
  kickOut(channelName: string, uid: number): Promise<ApiDataResponse<KickOutResponse>> {
    return super.post('KickOut', null, {
      params: {
        channelName,
        uid
      }
    });
  }
}
